import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { clearAuth } from "../../../store/authSlice";
import { persistor } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { ADMINPAGE } from "../../admin/routes";
import { SLOTS_MANAGER_PAGE } from "../../scheduling/routes";

import DenseForest from "../../../img/dense-forest.jpg";

import OpenSlotsIcon from "../../../img/calendar-plus.svg";
import ListSlotsIcon from "../../../img/calendar.svg";
import UpdateSlotsIcon from "../../../img/calendar-update.svg";
import ReportsIcon from "../../../img/reports.svg";
import { OPERACOES_PAGE } from "../../operacoes/routes";

type ActionKey = "cadastrar" | "consultar" | "alterar" | "excluir";

type ActionCard = {
  key: ActionKey;
  title: string;
  description: string;
  onClick: () => void;
  iconSrc?: string;
};

const AgendaPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = useSelector((state: RootState) => state.auth.accessToken);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const handleLogout = useCallback(async () => {
    dispatch(clearAuth());
    await persistor.purge();
    toast.success("Você saiu.");
    navigate(LOGINPAGE(), { replace: true });
  }, [dispatch, navigate]);

  const handleCadastrar = useCallback(() => {
    navigate(SLOTS_MANAGER_PAGE());
  }, [navigate]);

  const handleConsultar = useCallback(() => {
    navigate(SLOTS_MANAGER_PAGE());
  }, [navigate]);

  const handleAlterar = useCallback(() => {
    navigate(SLOTS_MANAGER_PAGE());
  }, [navigate]);

  const handleExcluir = useCallback(() => {
    navigate(SLOTS_MANAGER_PAGE());
  }, [navigate]);

  const actionCards: ActionCard[] = useMemo(
    () => [
      {
        key: "cadastrar",
        title: "Cadastrar",
        description: "Crie novos horários/slots na agenda",
        onClick: handleCadastrar,
        iconSrc: OpenSlotsIcon,
      },
      {
        key: "consultar",
        title: "Consultar",
        description: "Consulte horários/slots cadastrados",
        onClick: handleConsultar,
        iconSrc: ListSlotsIcon,
      },
      {
        key: "alterar",
        title: "Alterar",
        description: "Edite horários/slots já cadastrados",
        onClick: handleAlterar,
        iconSrc: UpdateSlotsIcon,
      },
      {
        key: "excluir",
        title: "Excluir",
        description: "Remova horários/slots da agenda",
        onClick: handleExcluir,
        iconSrc: ReportsIcon,
      },
    ],
    [handleCadastrar, handleConsultar, handleAlterar, handleExcluir],
  );

  if (!token) return null;

  return (
    <div className="homeLayout">
      <main className="homeMain">
        <section className="ecosystem">
          <img className="ecosystem__bg-img" src={DenseForest} alt="" aria-hidden="true" />
          <div className="ecosystem__content-bg" aria-hidden="true" />

          <div className="ecosystem__content">
            <header className="ecosystem__header">
              <h1 className="ecosystem__title">
                <span className="ecosystem__title-strong">Agenda </span>
                <span className="ecosystem__title-light">Gestão</span>
              </h1>

              <p className="ecosystem__subtitle">Escolha uma opção</p>
            </header>

            <div className="ecosystem__cards" role="list">
              {actionCards.map((card) => (
                <article key={card.key} className="ecosystem__card" role="listitem">
                  <div className="ecosystem__cardTop">
                    {card.iconSrc ? (
                      <img className="ecosystem__cardIcon" src={card.iconSrc} alt="" />
                    ) : (
                      <div className="ecosystem__cardIconPlaceholder" aria-hidden="true" />
                    )}

                    <div className="ecosystem__cardText">
                      <h2 className="ecosystem__cardTitle">{card.title}</h2>
                      <p className="ecosystem__cardDesc">{card.description}</p>
                    </div>
                  </div>

                  <button className="ecosystem__cardBtn" onClick={card.onClick}>
                    Acessar
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AgendaPage;
